/**
 * Swoopy API Service
 * Handles all backend API calls with loading states and error handling
 */

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Generic fetch wrapper with error handling
 */
async function fetchAPI(endpoint, options = {}) {
  const url = `${API_URL}${endpoint}`;
  
  const defaultOptions = {
    headers: {
      'Content-Type': 'application/json',
    },
  };
  
  const mergedOptions = {
    ...defaultOptions,
    ...options,
    headers: {
      ...defaultOptions.headers,
      ...options.headers,
    },
  };
  
  try {
    const response = await fetch(url, mergedOptions);
    
    if (!response.ok) {
      throw new Error(`API error: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error(`API call failed: ${endpoint}`, error);
    throw error;
  }
}

/**
 * Get autocomplete predictions for destination input
 */
export async function getAutocomplete(input, sessionToken = null) {
  if (!input || input.length < 2) {
    return { predictions: [] };
  }
  
  return fetchAPI('/api/places/autocomplete', {
    method: 'POST',
    body: JSON.stringify({
      input,
      session_token: sessionToken,
    }),
  });
}

/**
 * Get place details (name, lat/lng)
 */
export async function getPlaceDetails(placeId, sessionToken = null) {
  return fetchAPI('/api/places/details', {
    method: 'POST',
    body: JSON.stringify({
      place_id: placeId,
      session_token: sessionToken,
    }),
  });
}

/**
 * Search for places by type (hotels, restaurants, attractions)
 */
export async function searchPlaces(destination, placeType, options = {}) {
  return fetchAPI('/api/places/search', {
    method: 'POST',
    body: JSON.stringify({
      destination,
      place_type: placeType,
      latitude: options.latitude,
      longitude: options.longitude,
      limit: options.limit || 5,
    }),
  });
}

/**
 * Get all place types for trip enrichment
 */
export async function enrichTrip(destination, options = {}) {
  return fetchAPI('/api/places/enrich-trip', {
    method: 'POST',
    body: JSON.stringify({
      destination,
      latitude: options.latitude,
      longitude: options.longitude,
    }),
  });
}

/**
 * Generate AI itinerary
 */
export async function generateItinerary(destination, days, budget, vibe, selectedPois = []) {
  return fetchAPI('/api/itinerary', {
    method: 'POST',
    body: JSON.stringify({
      destination,
      days,
      budget,
      vibe,
      selected_pois: selectedPois,
    }),
  });
}

/**
 * Health check
 */
export async function checkHealth() {
  return fetchAPI('/api/health');
}

export default {
  getAutocomplete,
  getPlaceDetails,
  searchPlaces,
  enrichTrip,
  generateItinerary,
  checkHealth,
};
